import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, Link2, Loader2, Save, CalendarCheck, CalendarX } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { TimezoneSelect } from './SlotPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const bookingUrl = (slug) => `${window.location.origin}/book/${slug}`;

export const GcalStatusBadge = ({ status, testId = 'gcal-status' }) => {
  const on = status?.connected;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium rounded-full px-2 py-0.5 ${on ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`} data-testid={testId}>
      {on ? <CalendarCheck size={12} /> : <CalendarX size={12} />}
      {on ? `Connected${status.email ? ` · ${status.email}` : ''}` : status?.needs_reconnect ? 'Reconnect needed' : 'Disconnected'}
    </span>
  );
};

export const connectGoogleCalendar = (personnelId) => {
  const token = localStorage.getItem('token') || '';
  const q = new URLSearchParams({ personnel_id: personnelId, token, return_to: window.location.href });
  window.location.href = `${API}/oauth/google-calendar/start?${q}`;
};

const GcalRow = ({ data, onChanged }) => {
  const disconnect = async () => {
    await axios.post(`${API}/admin/personnel/${data.personnel_id}/google-calendar/disconnect`);
    toast.success('Google Calendar disconnected');
    onChanged();
  };
  const g = data.google_calendar;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border rounded-md p-3">
      <div className="space-y-1">
        <p className="text-sm font-medium">Google Calendar</p>
        <GcalStatusBadge status={g} />
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={() => connectGoogleCalendar(data.personnel_id)} data-testid="gcal-connect-btn">
          {g.connected ? 'Reconnect' : 'Connect'}
        </Button>
        {(g.connected || g.needs_reconnect) && <Button size="sm" variant="ghost" className="text-red-600" onClick={disconnect} data-testid="gcal-disconnect-btn">Disconnect</Button>}
      </div>
    </div>
  );
};

const NumField = ({ label, value, onChange, testId }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <Input type="number" min="0" value={value} onChange={(e) => onChange(e.target.value)} className="h-9" data-testid={testId} />
  </div>
);

export const BookingLinkPanel = ({ personnelId, onChange }) => {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => axios.get(`${API}/admin/personnel/${personnelId}/booking-settings`).then((r) => {
    setData(r.data);
    setDraft({ booking_slug: r.data.booking_slug, timezone: r.data.timezone, settings: { ...r.data.settings } });
  });
  useEffect(() => { load(); }, [personnelId]); // eslint-disable-line

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/personnel/${personnelId}/booking-settings`, patch);
      setData(r.data);
      setDraft({ booking_slug: r.data.booking_slug, timezone: r.data.timezone, settings: { ...r.data.settings } });
      toast.success('Booking link saved');
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  if (!data || !draft) return <div className="py-6 text-center"><Loader2 className="animate-spin inline" size={16} /></div>;
  const setS = (k) => (v) => setDraft({ ...draft, settings: { ...draft.settings, [k]: v } });
  const url = data.booking_slug ? bookingUrl(data.booking_slug) : '';

  return (
    <div className="space-y-4" data-testid="booking-link-panel">
      <div className="flex flex-wrap items-center gap-2 border rounded-md p-3 bg-muted/30">
        <Link2 size={15} className="text-primary" />
        <code className="text-xs break-all flex-1" data-testid="booking-link-url">{url || 'Save to create a link'}</code>
        <Button size="sm" variant="outline" disabled={!url} onClick={() => { navigator.clipboard?.writeText(url); toast.success('Link copied'); }} data-testid="booking-link-copy"><Copy size={13} className="mr-1" /> Copy link</Button>
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={data.booking_enabled} onCheckedChange={(v) => save({ booking_enabled: v })} data-testid="booking-link-toggle" />
          {data.booking_enabled ? 'On' : 'Off'}
        </label>
      </div>
      {!data.has_availability && <p className="text-xs text-amber-700">No weekly availability yet — set it so the link has open times.</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label className="text-xs">Link name</Label><Input className="h-9" value={draft.booking_slug} onChange={(e) => setDraft({ ...draft, booking_slug: e.target.value })} data-testid="booking-slug-input" /></div>
        <div><Label className="text-xs">Meeting title</Label><Input className="h-9" value={draft.settings.title || ''} placeholder={`Meeting with ${data.name}`} onChange={(e) => setS('title')(e.target.value)} data-testid="booking-title-input" /></div>
        <div className="sm:col-span-2"><Label className="text-xs">Interviewer timezone (availability is in this zone)</Label><TimezoneSelect value={draft.timezone} onChange={(v) => setDraft({ ...draft, timezone: v })} testId="booking-tz-select" /></div>
        <NumField label="Minimum notice (hours)" value={draft.settings.min_notice_hours} onChange={setS('min_notice_hours')} testId="booking-min-notice" />
        <NumField label="Max days ahead" value={draft.settings.max_days_ahead} onChange={setS('max_days_ahead')} testId="booking-max-days" />
        <NumField label="Buffer between meetings (min)" value={draft.settings.buffer_minutes} onChange={setS('buffer_minutes')} testId="booking-buffer" />
        <label className="flex items-center gap-2 text-sm pt-5">
          <input type="checkbox" checked={!!draft.settings.meet_default} onChange={(e) => setS('meet_default')(e.target.checked)} data-testid="booking-meet-default" /> Add a Google Meet link
        </label>
      </div>
      <Button size="sm" onClick={() => save(draft)} disabled={saving} data-testid="booking-settings-save"><Save size={13} className="mr-1" /> Save booking settings</Button>
      <GcalRow data={data} onChanged={load} />
    </div>
  );
};

// Shows a toast after returning from the Google Calendar connect flow.
export const useGcalReturnToast = () => {
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const g = q.get('gcal');
    if (!g) return;
    if (g === 'connected') toast.success(q.get('mock') ? 'Google Calendar connected (preview simulation)' : 'Google Calendar connected');
    else toast.error(`Google Calendar connection failed${q.get('reason') ? `: ${q.get('reason')}` : ''}`);
    ['gcal', 'mock', 'reason'].forEach((k) => q.delete(k));
    const qs = q.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
  }, []);
};
